import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './ImportInfoModal.css';

function ImportInfoModal({ isOpen, onClose, onChooseFile, onImportPasted }) {
  const [pastedText, setPastedText] = useState('');
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setPastedText('');
      setDetailsOpen(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChooseFile = () => {
    onClose();
    onChooseFile?.();
  };

  const handlePaste = (e) => {
    const text = (e.clipboardData?.getData('text') || '').trim();
    if (text) {
      onClose();
      onImportPasted?.(text);
    }
  };

  const handleBlur = () => {
    const text = pastedText.trim();
    if (text) {
      onClose();
      onImportPasted?.(text);
    }
  };

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const modalContent = (
    <div className="import-info-modal-backdrop" onClick={handleBackdropClick}>
      <div className="import-info-modal">
        <button className="import-info-modal-close text-non-selectable" onClick={onClose} aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4 L4 12 M4 4 L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </button>

        <h2>Import tasks</h2>
        <p className="import-info-modal-intro">
          Paste text below or choose a file. Each line becomes a task.
        </p>

        <div className="import-info-modal-tabs">
          <span className="import-info-modal-tab import-info-modal-tab-active text-non-selectable">Paste</span>
          <button type="button" className="import-info-modal-tab text-non-selectable" onClick={handleChooseFile}>
            Choose file
          </button>
        </div>

        <div className="import-info-modal-paste">
          <textarea
            id="import-paste-text"
            className="import-info-modal-paste-textarea"
            value={pastedText}
            onChange={(e) => setPastedText(e.target.value)}
            onPaste={handlePaste}
            onBlur={handleBlur}
            placeholder="Paste your tasks here..."
            rows={5}
          />
        </div>

        <div className="import-info-modal-details">
          <button
            type="button"
            className={`import-info-modal-details-summary text-non-selectable ${detailsOpen ? 'import-info-modal-details-summary-open' : ''}`}
            onClick={() => setDetailsOpen(!detailsOpen)}
            aria-expanded={detailsOpen}
          >
            Details
          </button>
          <div className={`import-info-modal-details-content ${detailsOpen ? 'import-info-modal-details-content-open' : ''}`}>
            <div className="import-info-modal-section">
              <h3>Format</h3>
              <ul>
                <li><strong>Indent</strong> — Tabs, 2 spaces, or 4 spaces for hierarchy (subtasks under the previous less-indented task).</li>
                <li><strong>List markers</strong> — Optional <code>- </code> or <code>* </code> at the start of a line (after indent).</li>
                <li><strong>Checkbox</strong> — <code>[ ]</code> incomplete, <code>[x]</code> done, <code>[w]</code> waiting, <code>[/]</code> in progress, <code>[-]</code> won&apos;t do.</li>
                <li><strong>Plain lines</strong> — Lines without a checkbox are treated as tasks (incomplete).</li>
                <li><strong>Due date</strong> — Add <code>(Jan 15)</code> or <code>(Dec 31)</code> at the end of the task text.</li>
              </ul>
            </div>

            <div className="import-info-modal-section">
              <h3>Examples</h3>
              <p className="import-info-modal-example-label">Checkboxes, hierarchy (tabs), due date:</p>
              <pre className="import-info-modal-example">
{`[ ] Buy groceries (Jan 15)
[ ] Plan trip
\t[ ] Book flight
\t[x] Book hotel
\t[ ] Pack bags
Review notes (Feb 1)`}
              </pre>
              <p className="import-info-modal-example-label">List markers with dash or star:</p>
              <pre className="import-info-modal-example">
{`- [ ] First task
- [x] Done task
* [ ] Another task`}
              </pre>
              <p className="import-info-modal-example-label">Spaces for indent (2 or 4):</p>
              <pre className="import-info-modal-example">
{`[ ] Parent
  [ ] Child with 2 spaces
    [ ] Grandchild with 4 spaces`}
              </pre>
              <p className="import-info-modal-example-label">All statuses: incomplete, done, waiting, in progress, won&apos;t do:</p>
              <pre className="import-info-modal-example">
{`[ ] To do
[x] Done
[w] Waiting
[/] In progress
[-] Won't do`}
              </pre>
              <p className="import-info-modal-example-label">Plain lines (no checkbox):</p>
              <pre className="import-info-modal-example">
{`Buy milk
Call mom (Mar 10)
- Finish report`}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

export default ImportInfoModal;
